import { FileText, HelpCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNodeValidation } from '../../hooks/useNodeValidation';
import type { IntegrationsNodeData } from '../../types/nodes';
import type { FormField } from '../FormBuilder/FormBuilder';
import { FormBuilder } from '../FormBuilder/FormBuilder';
import { FormPreview } from '../FormBuilder/FormPreview';
import { FormTemplates, type TemplateMetadata } from '../FormBuilder/FormTemplates';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';

export interface SubmissionFormConfigType {
	name: string;
	description: string;
	formType: 'embedded' | 'standalone';
	redirectUrl: string;
	fields: FormField[];
	templateId?: string; // Track which template was used
}

interface SubmissionFormConfigProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
		isCompletingSetup: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

type TabType = 'settings' | 'builder' | 'templates' | 'preview';

const defaultConfig: SubmissionFormConfigType = {
	name: '',
	description: '',
	formType: 'embedded',
	redirectUrl: '',
	fields: [],
	templateId: undefined,
};

export function SubmissionFormConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
}: SubmissionFormConfigProps) {
	const [activeTab, setActiveTab] = useState<TabType>('settings');
	const { validateNode, isValidating, lastValidationResult, clearValidationResult } =
		useNodeValidation();

	// Wizard state management
	const [wizardMode, setWizardMode] = useState(true); // Always start in wizard mode for submission forms
	const [currentStep, setCurrentStep] = useState(1); // Always start at step 1

	// Reset to step 1 whenever the component mounts or when editing
	useEffect(() => {
		// Always start on step 1 when editing a submission form node
		setCurrentStep(1);
		setActiveTab('settings');
		// Reset loading state
		setIsCompletingSetup(false);
	}, [nodeId]); // Reset when nodeId changes (i.e., when editing a different node)

	// Extract config from data.config, use defaults if not present
	const config = useMemo(() => {
		const existingConfig = (data.config as Partial<SubmissionFormConfigType>) || {};
		const mergedConfig = {
			...defaultConfig,
			...existingConfig,
		};

		return mergedConfig;
	}, [data.config, data.label]);

	// Step validation logic
	const canProceedToStep = useCallback(
		(step: number) => {
			switch (step) {
				case 1:
					return true; // Template selection - can always start
				case 2:
					return true; // Form configuration - can always proceed to configure
				case 3:
					return config.name.trim() !== ''; // Fields - need name before building fields
				case 4:
					return config.fields && config.fields.length > 0; // Preview - need at least one field
				default:
					return false;
			}
		},
		[config.name, config.fields]
	);

	// Immediately set up wizard state when component mounts to prevent race conditions
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode: true,
				currentStep,
				totalSteps: 4,
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
				onNextStep: () => setCurrentStep(currentStep + 1),
				onCompleteWizard: async () => {
					// This will be replaced by the full implementation below
					console.log('🚀 Submission Form: Initial onCompleteWizard called');
				},
				validationAttempted: false,
				validationFailed: false,
			});
		}
	}, [currentStep, onWizardStateChange]); // Include currentStep in dependencies

	// Validation state for two-step complete setup
	const [validationAttempted, setValidationAttempted] = useState(false); // Track if validation has been attempted
	const [validationFailed, setValidationFailed] = useState(false); // Track if validation failed on previous attempt

	// Loading state for complete setup button
	const [isCompletingSetup, setIsCompletingSetup] = useState(false);

	const updateConfig = useCallback(
		(updates: Partial<SubmissionFormConfigType>) => {
			console.log('🔍 updateConfig called with:', updates);
			console.log('🔍 Current data.config:', data.config);

			// Create the updated config by merging with existing data.config
			const existingConfig = (data.config as Partial<SubmissionFormConfigType>) || {};
			const updatedConfig = { ...existingConfig, ...updates };

			console.log('🔍 Updated config:', updatedConfig);

			// Update the data with the new config
			const updatedData = {
				...data,
				config: updatedConfig,
			};

			console.log('🔍 Calling onChange with:', updatedData);

			// Call onChange to update the parent
			onChange(updatedData);

			console.log('🔍 onChange called successfully');
		},
		[data, onChange]
	);

	// Initialize config.name with node label if not already set or if it's corrupted
	useEffect(() => {
		if (data.label && (!config.name || config.name !== data.label)) {
			console.log('🔍 Syncing config.name with node label:', data.label);
			updateConfig({ name: data.label });
		}
	}, [data.label, config.name, updateConfig]);

	// Removed automatic validation - only validate on save/exit
	// triggerValidation function and related timeout cleanup removed

	// Validation function that can be called by parent components
	const performValidation = useCallback(async (): Promise<boolean> => {
		try {
			const result = await validateNode(nodeId, data);

			// Return true if validation passed, false if there are critical errors
			return result.valid || !result.errors.some(error => error.severity === 'error');
		} catch (error) {
			console.error('Validation failed:', error);
			return false;
		}
	}, [validateNode, nodeId, data]);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Communicate wizard state to parent component (BaseNode) so drawer footer shows prev/next buttons
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode,
				currentStep,
				totalSteps: 4, // Form wizard has 4 steps
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
				onNextStep: () => setCurrentStep(currentStep + 1),
				onCompleteWizard: async () => {
					console.log('🚀 Submission Form: onCompleteWizard called');

					// Set loading state immediately
					setIsCompletingSetup(true);

					try {
						// Step 1: If validation hasn't been attempted yet, run validation and stay in node
						if (!validationAttempted) {
							try {
								console.log(
									'🔍 Starting validation for Submission Form node:',
									nodeId
								);

								// Use the existing validation function
								const isValid = await performValidation();
								console.log('✅ Validation function completed, result:', isValid);

								setValidationAttempted(true);
								console.log('🔄 Set validationAttempted to true');

								if (!isValid) {
									// Validation failed - stay in node and show errors
									setValidationFailed(true);
									console.log('❌ Form validation failed, staying in node');

									// Show validation errors in the UI
									if (
										lastValidationResult?.errors &&
										lastValidationResult.errors.length > 0
									) {
										console.log(
											'📋 Validation errors:',
											lastValidationResult.errors
										);
									}

									// IMPORTANT: Don't exit wizard mode, keep it open
									console.log('🔄 Keeping wizard open due to validation failure');
									setIsCompletingSetup(false); // Reset loading state
									return; // Don't exit the node
								} else {
									// Validation succeeded - proceed to exit
									setValidationFailed(false);
									console.log('✅ Form validation succeeded');
								}
							} catch (error) {
								console.error('❌ Form validation failed with error:', error);
								setValidationAttempted(true);
								setValidationFailed(true);

								// Show error message to user
								console.log('💥 Validation error occurred, staying in node');
								setIsCompletingSetup(false); // Reset loading state
								return; // Don't exit the node on error
							}
						}

						// Step 2: If validation was already attempted and succeeded, allow exit
						if (validationAttempted && !validationFailed) {
							console.log('🔄 Validation already succeeded, allowing exit');

							// Save the current configuration and exit wizard mode
							setWizardMode(false);
							// Don't reset currentStep immediately - let BaseNode close drawer first
							setTimeout(() => setCurrentStep(1), 100);

							// Reset validation state for next time
							setValidationAttempted(false);
							setValidationFailed(false);
						} else if (validationAttempted && validationFailed) {
							// Step 3: If validation failed but user wants to proceed anyway
							console.log('🔄 Validation failed but user wants to proceed anyway');

							// Save the current configuration and exit wizard mode
							setWizardMode(false);
							// Don't reset currentStep immediately - let BaseNode close drawer first
							setTimeout(() => setCurrentStep(1), 100);

							// Reset validation state for next time
							setValidationAttempted(false);
							setValidationFailed(false);
						}
					} finally {
						// Always reset loading state
						setIsCompletingSetup(false);
					}
				},
				validationAttempted,
				validationFailed,
				isCompletingSetup,
			});
		}
	}, [
		wizardMode,
		currentStep,
		config,
		onWizardStateChange,
		validationAttempted,
		validationFailed,
		performValidation,
		isCompletingSetup, // Add this dependency
	]);

	// Function to detect which template was used based on existing configuration
	const detectUsedTemplate = (): string | undefined => {
		// If templateId is already stored, use it
		if (config.templateId) {
			return config.templateId;
		}

		// Try to detect template based on form name and fields
		if (!config.fields || config.fields.length === 0) {
			return undefined;
		}

		// Map template names to their IDs (based on FormTemplates.tsx)
		const templateNames = {
			'Adventure Shield': 'adventure-shield',
			'Power Sports Insurance': 'power-sports',
			'Marine Insurance': 'marine',
			'Commercial Liability Insurance': 'commercial-liability',
		};

		// Check if the form name matches a template name
		const matchedTemplateId = templateNames[config.name as keyof typeof templateNames];
		if (matchedTemplateId) {
			return matchedTemplateId;
		}

		// If no direct match, return undefined (custom form)
		return undefined;
	};

	const selectedTemplateId = detectUsedTemplate();

	const handleTemplateSelect = (template: TemplateMetadata) => {
		updateConfig({
			name: template.name,
			description: template.description,
			fields: template.fields,
			templateId: template.id, // Store the selected template ID
		});
		if (wizardMode) {
			setCurrentStep(2); // Move to step 2 in wizard mode
		} else {
			setActiveTab('builder');
		}
	};

	// Tooltip Component for help text
	const Tooltip = ({ content, children }: { content: string; children: React.ReactNode }) => {
		const [showTooltip, setShowTooltip] = useState(false);

		return (
			<div className="inline-flex relative items-center">
				{children}
				<button
					type="button"
					onMouseEnter={() => setShowTooltip(true)}
					onMouseLeave={() => setShowTooltip(false)}
					className="ml-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
				>
					<HelpCircle className="w-4 h-4" />
				</button>
				{showTooltip && (
					<div className="absolute left-0 bottom-full z-50 p-2 mb-1 w-64 text-xs text-white bg-gray-900 rounded-md shadow-lg dark:bg-gray-700 dark:text-gray-100">
						{content}
						<div className="absolute left-2 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
					</div>
				)}
			</div>
		);
	};

	// Wizard Step Components
	const renderStep1ChooseTemplate = () => (
		<div className="space-y-6">
			<div className="text-center">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">
					Choose Template
				</h3>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Start with a pre-built template or create a custom form from scratch
				</p>
			</div>

			<FormTemplates
				onSelectTemplate={handleTemplateSelect}
				selectedTemplateId={selectedTemplateId}
			/>

			<div className="text-center">
				<button
					onClick={() => setCurrentStep(2)}
					className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md border border-blue-200 transition-colors dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50"
				>
					Skip Templates - Start from Scratch
				</button>
			</div>
		</div>
	);

	const renderStep2ConfigureForm = () => (
		<div className="space-y-6">
			<div className="text-center">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">
					Configure Form
				</h3>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Set up basic information about your form
				</p>
			</div>

			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div className="space-y-2">
					<Tooltip content="A descriptive name for your form that will be displayed to users">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Form Name <span className="text-red-500 dark:text-red-400">*</span>
						</label>
					</Tooltip>
					<input
						type="text"
						value={config.name || ''}
						onChange={e => {
							const newName = e.target.value;

							// Update the form name in the config
							updateConfig({ name: newName });
						}}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						placeholder="e.g., Contact Form, Insurance Quote"
					/>
				</div>

				<div className="space-y-2">
					<Tooltip content="Choose how your form will be displayed: embedded within a page or as a standalone form">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
							Form Type
						</label>
					</Tooltip>
					<select
						value={config.formType}
						onChange={e =>
							updateConfig({
								formType: e.target.value as 'embedded' | 'standalone',
							})
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
					>
						<option value="embedded">Embedded</option>
						<option value="standalone">Standalone</option>
					</select>
				</div>
			</div>

			<div className="space-y-2">
				<Tooltip content="Optional description that helps users understand the purpose of your form">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Description
					</label>
				</Tooltip>
				<textarea
					value={config.description}
					onChange={e => updateConfig({ description: e.target.value })}
					className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
					rows={3}
					placeholder="Brief description of what this form is for..."
				/>
			</div>

			<div className="space-y-2">
				<Tooltip content="URL where users will be redirected after successful form submission">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Redirect URL (Optional)
					</label>
				</Tooltip>
				<input
					type="url"
					value={config.redirectUrl}
					onChange={e => updateConfig({ redirectUrl: e.target.value })}
					className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
					placeholder="https://example.com/thank-you"
				/>
			</div>

			{/* Validation Status */}
			<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
				<h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
					✅ Step 2 Requirements
				</h4>
				<div className="flex gap-2 items-center text-sm">
					<div
						className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
							config.name.trim()
								? 'bg-green-500 text-white'
								: 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
						}`}
					>
						{config.name.trim() ? '✓' : '1'}
					</div>
					<span
						className={
							config.name.trim()
								? 'text-green-700 dark:text-green-300'
								: 'text-gray-600 dark:text-gray-400'
						}
					>
						Form name is required to proceed
					</span>
				</div>
			</div>
		</div>
	);

	const renderStep3BuildFields = () => (
		<div className="space-y-6">
			<div className="text-center">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">
					Build Form Fields
				</h3>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					Add and configure fields that users will fill out
				</p>
			</div>

			{/* Token Reference Info */}
			<div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 dark:from-green-900/20 dark:to-blue-900/20 dark:border-green-800">
				<div className="flex gap-3 items-start">
					<div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-green-100 rounded-full dark:bg-green-900/30">
						<span className="text-sm">🔗</span>
					</div>
					<div className="flex-1">
						<h4 className="mb-1 text-sm font-medium text-green-900 dark:text-green-200">
							Token Reference
						</h4>
						<p className="mb-2 text-xs text-green-700 dark:text-green-300">
							These fields become available as tokens in downstream nodes for use in
							emails, API calls, etc.
						</p>
						{config.fields.length > 0 && (
							<div className="space-y-1">
								<div className="text-xs font-medium text-green-800 dark:text-green-200">
									Available Tokens:
								</div>
								<div className="flex flex-wrap gap-1">
									{config.fields.slice(0, 5).map(field => (
										<code
											key={field.fieldId}
											className="px-2 py-1 font-mono text-xs text-green-700 bg-green-100 rounded dark:bg-green-900/40 dark:text-green-300"
										>
											{`{{${field.fieldId}}}`}
										</code>
									))}
									{config.fields.length > 5 && (
										<span className="px-2 py-1 text-xs text-green-600 dark:text-green-400">
											+{config.fields.length - 5} more
										</span>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			<FormBuilder fields={config.fields} onChange={fields => updateConfig({ fields })} />

			{/* Progress indicator */}
			<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
				<h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
					📝 Form Progress
				</h4>
				<div className="flex gap-2 items-center text-sm">
					<div
						className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
							config.fields.length > 0
								? 'bg-green-500 text-white'
								: 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
						}`}
					>
						{config.fields.length > 0 ? '✓' : '0'}
					</div>
					<span
						className={
							config.fields.length > 0
								? 'text-green-700 dark:text-green-300'
								: 'text-gray-600 dark:text-gray-400'
						}
					>
						{config.fields.length} field{config.fields.length !== 1 ? 's' : ''}{' '}
						configured
						{config.fields.length === 0 && ' (at least 1 required)'}
					</span>
				</div>
			</div>
		</div>
	);

	const renderStep4Preview = () => (
		<div className="space-y-6">
			<div className="text-center">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white">
					Preview & Test
				</h3>
				<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
					See how your form looks and test it with sample data
				</p>
			</div>

			{/* Form Summary */}
			<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
				<h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
					📋 Form Summary
				</h4>
				<div className="space-y-1 text-xs text-blue-700 dark:text-blue-300">
					<div>
						<strong>Name:</strong> {config.name || 'Untitled Form'}
					</div>
					<div>
						<strong>Type:</strong> {config.formType}
					</div>
					<div>
						<strong>Fields:</strong> {config.fields.length}
					</div>
					{config.redirectUrl && (
						<div>
							<strong>Redirect:</strong> {config.redirectUrl}
						</div>
					)}
				</div>
			</div>

			{/* Form Preview */}
			{config.fields.length > 0 ? (
				<FormPreview fields={config.fields} />
			) : (
				<div className="text-center py-8 text-gray-500 dark:text-gray-400">
					<FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
					<p>Add form fields in the Builder tab to see a preview</p>
				</div>
			)}

			{/* Token Reference */}
			{config.fields.length > 0 && (
				<div className="p-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
					<h4 className="mb-2 text-sm font-medium text-green-900 dark:text-green-200">
						🔗 Available Tokens
					</h4>
					<div className="mb-2 text-xs text-green-700 dark:text-green-300">
						Use these tokens in downstream Email, API, or Transform nodes:
					</div>
					<div className="flex flex-wrap gap-1">
						{config.fields.map(field => (
							<code
								key={field.fieldId}
								className="px-2 py-1 font-mono text-xs text-green-700 bg-green-100 rounded dark:bg-green-900/40 dark:text-green-300"
							>
								{`{{${field.fieldId}}}`}
							</code>
						))}
					</div>
				</div>
			)}

			{/* Completion Status */}
			<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
				<h4 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
					✨ Ready to Complete
				</h4>
				<div className="text-sm text-gray-600 dark:text-gray-400">
					Your form is configured and ready to use. Click "Complete Setup" to save and
					close.
				</div>
			</div>
		</div>
	);

	const tabs: { id: TabType; label: string }[] = [
		{ id: 'settings', label: 'Form Settings' },
		{ id: 'templates', label: 'Templates' },
		{ id: 'builder', label: 'Form Builder' },
		{ id: 'preview', label: 'Preview' },
	];

	// Wizard mode render
	if (wizardMode) {
		return (
			<div className="flex flex-col h-full">
				{/* Wizard Header with Progress */}
				<div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 dark:border-gray-700 dark:from-blue-900/20 dark:to-purple-900/20">
					<div className="flex justify-between items-center mb-3">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
							Form Setup Wizard
						</h2>
					</div>

					{/* Progress Indicator */}
					<div className="flex gap-2 items-center">
						{[1, 2, 3, 4].map((step, index) => (
							<div key={step} className="flex items-center">
								<div
									className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
										currentStep === step
											? 'bg-blue-600 text-white shadow-lg scale-110'
											: currentStep > step
											? 'bg-green-500 text-white'
											: 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
									}`}
								>
									{currentStep > step ? '✓' : step}
								</div>
								{index < 3 && (
									<div
										className={`w-8 h-1 mx-1 rounded-full transition-all ${
											currentStep > step
												? 'bg-green-500'
												: 'bg-gray-300 dark:bg-gray-600'
										}`}
									/>
								)}
							</div>
						))}
					</div>

					{/* Step Labels */}
					<div className="flex justify-between items-center mt-2 text-xs text-gray-600 dark:text-gray-400">
						<span
							className={
								currentStep === 1
									? 'font-medium text-blue-600 dark:text-blue-400'
									: ''
							}
						>
							Choose Template
						</span>
						<span
							className={
								currentStep === 2
									? 'font-medium text-blue-600 dark:text-blue-400'
									: ''
							}
						>
							Configure Form
						</span>
						<span
							className={
								currentStep === 3
									? 'font-medium text-blue-600 dark:text-blue-400'
									: ''
							}
						>
							Build Fields
						</span>
						<span
							className={
								currentStep === 4
									? 'font-medium text-blue-600 dark:text-blue-400'
									: ''
							}
						>
							Preview
						</span>
					</div>
				</div>

				{/* Wizard Content */}
				<div className="overflow-auto flex-1 p-6">
					{/* Validation Results */}
					{(isValidating || lastValidationResult) && (
						<>
							{console.log('🎯 Rendering ValidationResultDisplay:', {
								isValidating,
								lastValidationResult,
							})}
							<ValidationResultDisplay
								errors={lastValidationResult?.errors || []}
								isValidating={isValidating}
								onDismiss={clearValidationResult}
							/>
						</>
					)}

					{currentStep === 1 && renderStep1ChooseTemplate()}
					{currentStep === 2 && renderStep2ConfigureForm()}
					{currentStep === 3 && renderStep3BuildFields()}
					{currentStep === 4 && renderStep4Preview()}
				</div>
			</div>
		);
	}

	// Traditional tabbed mode render
	return (
		<div className="flex flex-col h-full">
			{/* Header with mode toggle */}
			<div className="px-6 py-3 bg-gray-50 border-b border-gray-200 dark:border-gray-700 dark:bg-gray-800">
				<div className="flex justify-between items-center">
					<h3 className="text-sm font-medium text-gray-900 dark:text-white">
						Submission Form Configuration
					</h3>
					<button
						onClick={() => setWizardMode(true)}
						className="text-sm text-blue-600 underline dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
					>
						Switch to Wizard Mode
					</button>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 dark:border-gray-700">
				{tabs.map(tab => (
					<button
						key={tab.id}
						className={`px-4 py-2 font-medium ${
							activeTab === tab.id
								? 'border-b-2 border-primary text-primary'
								: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
						}`}
						onClick={() => setActiveTab(tab.id)}
					>
						{tab.label}
					</button>
				))}
			</div>

			{/* Content */}
			<div className="overflow-auto flex-1">
				{/* Validation Results */}
				{(isValidating || lastValidationResult) && (
					<>
						{console.log('🎯 Rendering ValidationResultDisplay:', {
							isValidating,
							lastValidationResult,
						})}
						<ValidationResultDisplay
							errors={lastValidationResult?.errors || []}
							isValidating={isValidating}
							onDismiss={clearValidationResult}
						/>
					</>
				)}

				{activeTab === 'settings' && (
					<div className="p-6 space-y-4">
						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Form Name
							</label>
							<input
								type="text"
								value={config.name}
								onChange={e => updateConfig({ name: e.target.value })}
								className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								placeholder="Enter form name"
							/>
						</div>

						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Description
							</label>
							<textarea
								value={config.description}
								onChange={e => updateConfig({ description: e.target.value })}
								className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								rows={3}
								placeholder="Enter form description"
							/>
						</div>

						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Form Type
							</label>
							<select
								value={config.formType}
								onChange={e =>
									updateConfig({
										formType: e.target.value as 'embedded' | 'standalone',
									})
								}
								className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							>
								<option value="embedded">Embedded</option>
								<option value="standalone">Standalone</option>
							</select>
						</div>

						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Redirect URL
							</label>
							<input
								type="url"
								value={config.redirectUrl}
								onChange={e => updateConfig({ redirectUrl: e.target.value })}
								className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								placeholder="https://example.com/thank-you"
							/>
						</div>
					</div>
				)}

				{activeTab === 'templates' && (
					<FormTemplates
						onSelectTemplate={handleTemplateSelect}
						selectedTemplateId={selectedTemplateId}
					/>
				)}

				{activeTab === 'builder' && (
					<FormBuilder
						fields={config.fields}
						onChange={fields => updateConfig({ fields })}
					/>
				)}

				{activeTab === 'preview' && <FormPreview fields={config.fields} />}
			</div>
		</div>
	);
}
